import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PlayerData, PlayerPosition } from "../../types";
import { apiClient } from "../../lib/api";
import {
  buildPlayerCompletionPayload,
  fetchPlayerTeamAssignments,
  isJerseyNumberMissing,
  updateTeamPlayerJerseyNumber,
  type PlayerMissingField,
  type PlayerTeamAssignment,
} from "../../lib/players";
import CountrySelect from "../CountrySelect";
import { usePositionReferenceOptions } from "../../hooks/usePositionReferenceOptions";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import Alert from "../Alert";

/** Radix `Select.Item` forbids an empty-string value — same sentinel
 *  pattern used throughout the admin managers (see PlayersManager.tsx). */
const UNSET = "__unset__";

export interface PlayerCompletionRowProps {
  player: PlayerData;
  missingFields: PlayerMissingField[];
  colSpan: number;
  onCancel: () => void;
  /** Called after a successful save — caller refetches the player list so
   *  the row's badge/is_provisional state reflects the server. */
  onSaved: () => void | Promise<void>;
}

/**
 * Inline "complete profile" panel rendered as an extra `<tr>` directly
 * under a player's row in PlayersManager (EPIC-95 AC7) — expands within
 * the players view itself rather than opening the full edit modal, and is
 * scoped to only the fields actually missing: the three player-level
 * completeness fields (birth date, nationality, position — see
 * `computeMissingPlayerFields`) plus, independently, any team-roster link
 * with no jersey number set yet.
 *
 * Saving the player-level fields reuses `PUT /players/{id}` — the backend
 * auto-clears `is_provisional` once birth_date + country_name + position
 * are ALL set (app/routers/players.py `update_player`); jersey numbers are
 * a separate, per-team-link concept and are saved via
 * `PUT /teams/{team_id}/players/{player_id}` — see `players.ts`'s doc
 * comments for why the two are not conflated.
 */
export default function PlayerCompletionRow({
  player,
  missingFields,
  colSpan,
  onCancel,
  onSaved,
}: PlayerCompletionRowProps) {
  const { t } = useTranslation("admin");
  const [birthDate, setBirthDate] = useState("");
  const [countryName, setCountryName] = useState("");
  const [position, setPosition] = useState<PlayerPosition | "">("");
  const positionOptions = usePositionReferenceOptions(position || undefined);

  const [teams, setTeams] = useState<PlayerTeamAssignment[] | null>(null);
  const [teamsError, setTeamsError] = useState<string | null>(null);
  const [jerseyEdits, setJerseyEdits] = useState<Record<string, string>>({});

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const missingBirthDate = missingFields.includes("birth_date");
  const missingCountry = missingFields.includes("country_name");
  const missingPosition = missingFields.includes("position");

  useEffect(() => {
    let cancelled = false;
    setTeams(null);
    setTeamsError(null);
    fetchPlayerTeamAssignments(player.player_id)
      .then((assignments) => {
        if (cancelled) return;
        setTeams(assignments);
      })
      .catch(() => {
        if (cancelled) return;
        setTeamsError(t("missingFields.teamsLoadError"));
        setTeams([]);
      });
    return () => {
      cancelled = true;
    };
    // `t` deliberately excluded: react-i18next's `t` is stable across
    // re-renders for a given language in real usage, but including it here
    // would re-run this network fetch on every render whose `t` identity
    // happens to change (e.g. a language switch mid-fetch) for no benefit —
    // the fetched team data itself carries no translated strings.
  }, [player.player_id]);

  const jerseyMissingTeams = (teams ?? []).filter(isJerseyNumberMissing);
  const today = new Date().toISOString().split("T")[0];

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const profilePayload = buildPlayerCompletionPayload({
        birth_date: missingBirthDate ? birthDate : undefined,
        country_name: missingCountry ? countryName : undefined,
        position: missingPosition ? position : undefined,
      });
      if (Object.keys(profilePayload).length > 0) {
        await apiClient.put(`/players/${player.player_id}`, profilePayload);
      }

      const jerseyUpdates = Object.entries(jerseyEdits)
        .map(([teamId, raw]) => [teamId, parseInt(raw, 10)] as const)
        .filter(([, value]) => Number.isFinite(value) && value > 0);
      for (const [teamId, value] of jerseyUpdates) {
        await updateTeamPlayerJerseyNumber(teamId, player.player_id, value);
      }

      await onSaved();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setSaveError(
        typeof detail === "string" ? detail : t("missingFields.saveError"),
      );
    } finally {
      setSaving(false);
    }
  };

  const nothingToSave =
    (!missingBirthDate || !birthDate) &&
    (!missingCountry || !countryName) &&
    (!missingPosition || !position) &&
    Object.entries(jerseyEdits).every(
      ([, raw]) => !raw || !Number.isFinite(parseInt(raw, 10)),
    );

  return (
    <tr
      className="bg-amber-50/60"
      data-testid={`player-completion-row-${player.player_id}`}
    >
      <td colSpan={colSpan} className="px-4 py-4">
        <div className="space-y-3">
          {saveError && (
            <Alert tone="error" onDismiss={() => setSaveError(null)}>
              {saveError}
            </Alert>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {missingBirthDate && (
              <div>
                <label
                  htmlFor={`completion-birth-date-${player.player_id}`}
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  {t("birthDate")}
                </label>
                <input
                  id={`completion-birth-date-${player.player_id}`}
                  type="date"
                  max={today}
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  data-testid={`completion-birth-date-${player.player_id}`}
                  className="input w-full"
                />
              </div>
            )}
            {missingCountry && (
              <div>
                <label
                  htmlFor={`completion-country-${player.player_id}`}
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  {t("country")}
                </label>
                <CountrySelect
                  id={`completion-country-${player.player_id}`}
                  value={countryName}
                  onChange={setCountryName}
                  placeholder={t("selectCountry")}
                  data-testid={`completion-country-${player.player_id}`}
                />
              </div>
            )}
            {missingPosition && (
              <div>
                <label
                  htmlFor={`completion-position-${player.player_id}`}
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  {t("position")}
                </label>
                <Select
                  value={position === "" ? UNSET : position}
                  onValueChange={(next) =>
                    setPosition((next === UNSET ? "" : next) as PlayerPosition)
                  }
                >
                  <SelectTrigger
                    id={`completion-position-${player.player_id}`}
                    data-testid={`completion-position-${player.player_id}`}
                  >
                    <SelectValue
                      placeholder={t("quickCreatePlayer.selectPosition")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNSET}>
                      {t("quickCreatePlayer.selectPosition")}
                    </SelectItem>
                    {positionOptions.groupOrder.map((group) =>
                      positionOptions.grouped[group].length === 0 ? null : (
                        <SelectGroup key={group}>
                          <SelectLabel>
                            {t(`referenceData.positionGroups.${group}`)}
                          </SelectLabel>
                          {positionOptions.grouped[group].map((opt) => (
                            <SelectItem key={opt.code} value={opt.code}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {teamsError && <p className="text-xs text-red-600">{teamsError}</p>}
          {teams === null && !teamsError && (
            <p className="text-xs text-gray-400">
              {t("missingFields.loadingTeams")}
            </p>
          )}
          {jerseyMissingTeams.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-700 mb-1">
                {t("missingFields.jerseyNumberPerTeam")}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {jerseyMissingTeams.map((assignment) => (
                  <div key={assignment.team_id}>
                    <label
                      htmlFor={`completion-jersey-${player.player_id}-${assignment.team_id}`}
                      className="block text-xs text-gray-500 mb-1"
                    >
                      {assignment.team_name}
                    </label>
                    <input
                      id={`completion-jersey-${player.player_id}-${assignment.team_id}`}
                      type="number"
                      min={1}
                      max={99}
                      placeholder={t("jerseyNumber")}
                      value={jerseyEdits[assignment.team_id] ?? ""}
                      onChange={(e) =>
                        setJerseyEdits((prev) => ({
                          ...prev,
                          [assignment.team_id]: e.target.value,
                        }))
                      }
                      data-testid={`completion-jersey-${player.player_id}-${assignment.team_id}`}
                      className="input w-full"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary"
              disabled={saving}
            >
              {t("cancel")}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || nothingToSave}
              data-testid={`completion-save-${player.player_id}`}
              className={`btn btn-primary ${
                saving || nothingToSave ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {saving ? t("loading") : t("missingFields.save")}
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}
